import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const DEBOUNCE_MS = 350;

/** Filter state for the admin tables, stored in the URL rather than component state.
 *  The URL is what makes `?owner_id=<uuid>` a plain navigation from All Users, and it
 *  gets shareable links and a working back button for free — provided keystrokes REPLACE
 *  history instead of pushing it (see setSearch). */
export const useAdminFilters = (keys) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlQ = searchParams.get("q") || "";
  const [searchText, setSearchText] = useState(urlQ);

  // The URL is the source of truth: if it changes from outside (back button, a link into
  // the page), the input must follow it.
  useEffect(() => {
    setSearchText(urlQ);
  }, [urlQ]);

  const setSearch = useCallback(
    (value) => {
      setSearchText(value);   // immediate, so typing feels instant
    },
    [],
  );

  // Debounce the URL write, not the input. Typing "javascript" would otherwise push ten
  // history entries and Back would walk the user through "javascrip", "javascri", ...
  useEffect(() => {
    if (searchText === urlQ) return undefined;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (searchText) next.set("q", searchText);
      else next.delete("q");
      next.delete("page");
      setSearchParams(next, { replace: true });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText, urlQ, searchParams, setSearchParams]);

  // A dropdown pick is a discrete, deliberate act — worth its own history entry.
  const setFilter = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete("page");
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const clearAll = useCallback(() => {
    setSearchText("");
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const filters = {};
  keys.forEach((key) => {
    filters[key] = searchParams.get(key) || "";
  });

  const page = Number(searchParams.get("page") || 1);
  const setPage = useCallback(
    (nextPage) => {
      const next = new URLSearchParams(searchParams);
      if (nextPage > 1) next.set("page", String(nextPage));
      else next.delete("page");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const hasFilters = keys.some((key) => searchParams.get(key));

  return { filters, searchText, setSearch, setFilter, clearAll, page, setPage, hasFilters };
};

export default useAdminFilters;
